import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import BridgePasswordModal from './components/BridgePasswordModal';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import DocumentsPage from './pages/DocumentsPage';
import ContractReviewPage from './pages/ContractReviewPage';
import CommunityPage from './pages/CommunityPage';
import LegalToolsPage from './pages/LegalToolsPage';
import ComparisonPage from './pages/ComparisonPage';
import TemplatesPage from './pages/TemplatesPage';
import CasesPage from './pages/CasesPage';
import IntelligenceDashboardPage from './pages/IntelligenceDashboardPage';
import AnnotatorPage from './pages/AnnotatorPage';
import SharedAnnotatorPage from './pages/SharedAnnotatorPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, authLoading } = useAuth();
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-900">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
          <span className="text-sm text-slate-400">Loading...</span>
        </div>
      </div>
    );
  }
  if (!currentUser) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  const { authLoading, bridgeNeedsPassword, bridgeTokenInfo, bridgeProcessing, bridgeError, completeBridgeLogin, cancelBridgeLogin } = useAuth();
  const { currentUser } = useAuth();
  const hasBridgeToken = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('talkoru_token');
  const waitingForBridge = hasBridgeToken && !bridgeNeedsPassword && !bridgeError;

  if (authLoading || waitingForBridge) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-900">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
          <span className="text-sm text-slate-400">{waitingForBridge ? 'Processing login...' : 'Initializing...'}</span>
        </div>
      </div>
    );
  }

  return (
    <Router>
      {bridgeNeedsPassword && bridgeTokenInfo && (
        <BridgePasswordModal tokenInfo={bridgeTokenInfo} isProcessing={bridgeProcessing} error={bridgeError}
          onSubmit={completeBridgeLogin} onCancel={cancelBridgeLogin} />
      )}
      <Routes>
        <Route path="/" element={currentUser ? <Navigate to="/app/dashboard" replace /> : <LandingPage />} />
        <Route path="/login" element={currentUser ? <Navigate to="/app/dashboard" replace /> : <LoginPage />} />
        <Route path="/app/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/app/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
        <Route path="/app/review" element={<ProtectedRoute><ContractReviewPage /></ProtectedRoute>} />
        <Route path="/app/community" element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />
        <Route path="/app/tools" element={<ProtectedRoute><LegalToolsPage /></ProtectedRoute>} />
        <Route path="/app/compare" element={<ProtectedRoute><ComparisonPage /></ProtectedRoute>} />
        <Route path="/app/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
        <Route path="/app/cases" element={<ProtectedRoute><CasesPage /></ProtectedRoute>} />
        <Route path="/app/intel" element={<ProtectedRoute><IntelligenceDashboardPage /></ProtectedRoute>} />
        <Route path="/app/documents/:docId/annotate" element={<ProtectedRoute><AnnotatorPage /></ProtectedRoute>} />
        <Route path="/share/:shareToken" element={<SharedAnnotatorPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
